// Globalny wskaźnik porady szybkiej w tle (karta PORADA-TLE, 27.10.2026) — dla klasyka i cockpitu, których strony porad
// są zamrożone. Montowany w App.js POZA AppShell (cockpit nie ma AppShell ani AIProgressBar). Nowy UI (/nowe) ma własną
// tackę zadań, więc tam nie jest renderowany. Źródło danych: `lib/postepPorady.js` (zasila je `lib/api.js`).
//
// Pokazuje WYŁĄCZNIE to, co policzono: czas od startu (zegar przeglądarki od 202), etap zapisany przez backend,
// czas od ostatniej ZMIANY etapu oraz czas od ostatniego udanego odczytu stanu. Po 60 s bez zmiany etapu napis
// „brak aktualizacji postępu od N s” — bez twierdzenia, że zadanie „działa”. Stan końcowy znika po 4 s.
import { useEffect, useState, useSyncExternalStore } from "react";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import {
  subskrybuj, stanPostepu, opis, formatCzas, wyczysc, CZAS_POKAZU_KONCA_MS,
} from "@/lib/postepPorady";

const sekundy = (ms) => Math.floor(ms / 1000);

export function tekstyWskaznika(s, teraz) {
  const o = opis(s, teraz);
  const nazwa = s.rodzaj === "deep" ? "Porada pogłębiona" : "Porada szybka";
  const tytul = s.status === "trwa" ? `${nazwa} — trwa w tle`
    : s.status === "gotowe" ? `${nazwa} — gotowa` : `${nazwa} — zakończona błędem`;
  const czas = `czas od startu: ${formatCzas(o.czasOdStartuMs)}`;
  const etap = s.etap
    ? [s.etap.numer !== null && s.etap.razem !== null ? `etap ${s.etap.numer} z ${s.etap.razem}` : s.etap.numer !== null ? `etap ${s.etap.numer}` : null, s.etap.etykieta]
      .filter(Boolean).join(" · ")
    : "serwer nie zapisał jeszcze żadnego etapu";
  let swiezosc = null;
  if (s.status === "trwa") {
    if (o.brakAktualizacji) {
      swiezosc = `brak aktualizacji postępu od ${sekundy(o.odAktualizacjiMs ?? o.czasOdStartuMs)} s`;
    } else if (o.odAktualizacjiMs !== null) {
      swiezosc = `ostatnia aktualizacja postępu ${sekundy(o.odAktualizacjiMs)} s temu`;
    }
  }
  const sprawdzenie = s.status === "trwa"
    ? (o.odSprawdzeniaMs === null ? "stan zadania jeszcze nie został odczytany" : `stan zadania odczytany ${sekundy(o.odSprawdzeniaMs)} s temu`)
    : null;
  return { tytul, czas, etap, swiezosc, sprawdzenie, brakAktualizacji: o.brakAktualizacji, procent: s.etap?.procent ?? null };
}

export default function PostepPoradySzybkiej() {
  const s = useSyncExternalStore(subskrybuj, stanPostepu, stanPostepu);
  const [teraz, setTeraz] = useState(() => Date.now());

  useEffect(() => {
    if (!s || s.status !== "trwa") return undefined;
    const id = setInterval(() => setTeraz(Date.now()), 1000);
    return () => clearInterval(id);
  }, [s]);

  useEffect(() => {
    if (!s || s.status === "trwa") return undefined;
    const id = setTimeout(() => wyczysc(), CZAS_POKAZU_KONCA_MS);
    return () => clearTimeout(id);
  }, [s]);

  if (!s) return null;
  const t = tekstyWskaznika(s, s.status === "trwa" ? teraz : s.koniec ?? teraz);
  const Ikona = s.status === "trwa" ? Loader2 : s.status === "gotowe" ? CheckCircle2 : AlertTriangle;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="postep-porady-szybkiej"
      data-status={s.status}
      data-brak-aktualizacji={t.brakAktualizacji ? "1" : "0"}
      className="fixed bottom-4 left-4 z-[70] w-[380px] max-w-[92vw] rounded-xl border border-border bg-card p-3 text-sm text-card-foreground shadow-2xl"
    >
      <div className="flex items-start gap-2">
        <Ikona className={`mt-0.5 h-4 w-4 shrink-0 ${s.status === "trwa" ? "animate-spin" : ""}`} aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="font-medium">{t.tytul}</p>
          <p className="text-xs text-muted-foreground">{t.czas}</p>
          <p className="text-xs" data-testid="postep-etap">{t.etap}</p>
          {t.swiezosc && (
            <p className={`text-xs ${t.brakAktualizacji ? "font-medium text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`} data-testid="postep-swiezosc">
              {t.swiezosc}
            </p>
          )}
          {t.sprawdzenie && <p className="text-xs text-muted-foreground" data-testid="postep-sprawdzenie">{t.sprawdzenie}</p>}
        </div>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-muted" aria-hidden="true">
        <div
          className={`h-full rounded bg-primary transition-[width] ${t.procent === null && s.status === "trwa" ? "animate-pulse" : ""}`}
          style={{ width: `${s.status === "gotowe" ? 100 : t.procent ?? (s.status === "trwa" ? 15 : 0)}%` }}
        />
      </div>
    </div>
  );
}
